import { maintenancePriceCalculatorAnalytics } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Re-export the stored jsonb shapes so the frontend can type buildings/
// addresses from the getById response (drizzle-zod leaves jsonb loosely typed).
export type { RyhtiBuilding, StoredAddress } from '@markku/db'

// Shape the website's address typeahead (AddressMatch) sends per address.
// Every field is bounded: the request is public + unauthenticated, and the
// values flow into an external Ryhti query and a DB insert, so caps keep one
// request from fanning out into unbounded outbound fetches or row bloat.
export const maintenancePriceCalculatorAddressSchema = z.object({
  buildingKeys: z.array(z.string().max(100)).max(50),
  street: z.string().max(200),
  number: z.number().int(),
  letter: z.string().max(10).nullable(),
  postalCode: z.string().max(20),
  postalOffice: z.string().max(100),
  municipalityNumber: z.string().max(10),
})

// Request body for POST /api/maintenance-price-calculator/estimate.
export const maintenancePriceCalculatorEstimateInputSchema = z.object({
  addresses: z.array(maintenancePriceCalculatorAddressSchema).min(1).max(20),
  // Optional attribution snapshot. The marketing site reads the mk_attr
  // cookie (AttributionPayload JSON) and passes it through; legacy clients
  // omit it and we just keep the columns null. Kept permissive (parsed
  // tolerantly server-side by parseAttributionPayload) so a malformed /
  // legacy cookie degrades to null instead of 400ing the estimate request.
  attribution: z.unknown().nullish(),
})

export const selectMaintenancePriceCalculatorAnalyticsSchema =
  createSelectSchema(maintenancePriceCalculatorAnalytics, {
    // Internal fields are filtered out for non-privileged users — mark
    // optional so the stripped response still passes Zod validation.
    internalFirstTouchAcquisition: z.unknown().nullable().optional(),
    internalLastTouchAcquisition: z.unknown().nullable().optional(),
  })
export const insertMaintenancePriceCalculatorAnalyticsSchema =
  createInsertSchema(maintenancePriceCalculatorAnalytics).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })

export type MaintenancePriceCalculatorAddress = z.infer<
  typeof maintenancePriceCalculatorAddressSchema
>
export type MaintenancePriceCalculatorEstimateInput = z.infer<
  typeof maintenancePriceCalculatorEstimateInputSchema
>
export type MaintenancePriceCalculatorAnalytics = z.infer<
  typeof selectMaintenancePriceCalculatorAnalyticsSchema
>

// ── ERP analytics views ────────────────────────────────────────────────────
// One canonical estimate after grouping refinement/duplicate rows (see
// groupedEstimates.ts). The calculator writes a new row on every address
// add/remove, so the ERP collapses rows sharing (ip, primary address, day).

export const maintenancePriceCalculatorListItemSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  primaryStreet: z.string(),
  primaryNumber: z.number(),
  primaryLetter: z.string().nullable(),
  primaryPostalCode: z.string(),
  primaryCity: z.string(),
  addressCount: z.number(),
  apartmentCount: z.number(),
  floorArea: z.number(),
  priceRangeLow: z.number().nullable(),
  priceRangeHigh: z.number().nullable(),
  shared: z.boolean(),
  sharedEmail: z.string().nullable(),
  sharedAt: z.string().nullable(),
  clickCount: z.number(),
  shortUrlKey: z.string().nullable(),
})

export const maintenancePriceCalculatorListInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(25),
  sortBy: z
    .enum([
      'createdAt',
      'apartmentCount',
      'floorArea',
      'priceRangeLow',
      'clickCount',
    ])
    .default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  // Free-text search over address (street/number/postal/city) and contact email.
  search: z.string().max(200).optional(),
})

export const maintenancePriceCalculatorListResultSchema = z.object({
  records: z.array(maintenancePriceCalculatorListItemSchema),
  total: z.number(),
})

// One address aggregated across all its estimates — the "hot addresses"
// traction view for sales. sessions counts canonical estimates (repeat
// estimation = signal); sessionsRecent is the subset within the last 7 days
// (timely "acting now" context); linkOpens sums shared-link clicks.
export const maintenancePriceCalculatorHotAddressSchema = z.object({
  primaryStreet: z.string(),
  primaryNumber: z.number(),
  primaryLetter: z.string().nullable(),
  primaryPostalCode: z.string(),
  primaryCity: z.string(),
  sessions: z.number(),
  sessionsRecent: z.number(),
  linkOpens: z.number(),
  sharedCount: z.number(),
  apartmentCount: z.number(),
  floorArea: z.number(),
  priceRangeLow: z.number().nullable(),
  priceRangeHigh: z.number().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
})

export const maintenancePriceCalculatorHotAddressInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(25),
  sortBy: z
    .enum(['sessions', 'sessionsRecent', 'linkOpens', 'lastSeenAt'])
    .default('sessions'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().max(200).optional(),
})

export const maintenancePriceCalculatorHotAddressResultSchema = z.object({
  records: z.array(maintenancePriceCalculatorHotAddressSchema),
  total: z.number(),
})

export const maintenancePriceCalculatorDashboardSchema = z.object({
  totals: z.object({
    estimatesThisWeek: z.number(),
    estimatesLastWeek: z.number(),
    sharedThisWeek: z.number(),
    sharedTotal: z.number(),
    clicksTotal: z.number(),
  }),
})

// Rolling daily series for the chart (GA-style 7 / 28 day window). One point
// per day, oldest first, with estimate and share counts.
export const maintenancePriceCalculatorDailySeriesInputSchema = z.object({
  days: z.union([z.literal(7), z.literal(28)]).default(7),
})

export const maintenancePriceCalculatorDailySeriesSchema = z.object({
  days: z.number(),
  series: z.array(
    z.object({
      date: z.string(),
      label: z.string(),
      estimates: z.number(),
      shares: z.number(),
      // Same day-index in the previous window (GA-style period comparison).
      estimatesPrev: z.number(),
      sharesPrev: z.number(),
    }),
  ),
})

export type MaintenancePriceCalculatorListItem = z.infer<
  typeof maintenancePriceCalculatorListItemSchema
>
export type MaintenancePriceCalculatorListInput = z.infer<
  typeof maintenancePriceCalculatorListInputSchema
>
export type MaintenancePriceCalculatorListResult = z.infer<
  typeof maintenancePriceCalculatorListResultSchema
>
// Detail-view enrichment: link opens, repeat estimates for the same address,
// and the contact resolved against our users (so we surface a phone we hold).
export const maintenancePriceCalculatorEstimateContextSchema = z.object({
  opens: z
    .object({ clickCount: z.number(), openedAt: z.string().nullable() })
    .nullable(),
  sameAddress: z.object({
    others: z.number(),
    lastEstimatedAt: z.string().nullable(),
  }),
  contact: z
    .object({
      email: z.string(),
      name: z.string().nullable(),
      phoneNumber: z.string().nullable(),
      // Resolved against our users — null when the email isn't a known user.
      userId: z.number().nullable(),
      // That user's existing CRM context (so we can call/email against it).
      opportunities: z.array(
        z.object({
          id: z.number(),
          type: z.string(),
          stage: z.string(),
          contextName: z.string().nullable(),
          user: z
            .object({ firstName: z.string(), lastName: z.string() })
            .nullable(),
        }),
      ),
      customers: z.array(
        z.object({ id: z.number(), name: z.string(), type: z.string() }),
      ),
    })
    .nullable(),
})

export type MaintenancePriceCalculatorDashboard = z.infer<
  typeof maintenancePriceCalculatorDashboardSchema
>
export type MaintenancePriceCalculatorEstimateContext = z.infer<
  typeof maintenancePriceCalculatorEstimateContextSchema
>
export type MaintenancePriceCalculatorDailySeriesInput = z.infer<
  typeof maintenancePriceCalculatorDailySeriesInputSchema
>
export type MaintenancePriceCalculatorDailySeries = z.infer<
  typeof maintenancePriceCalculatorDailySeriesSchema
>
export type MaintenancePriceCalculatorHotAddress = z.infer<
  typeof maintenancePriceCalculatorHotAddressSchema
>
export type MaintenancePriceCalculatorHotAddressInput = z.infer<
  typeof maintenancePriceCalculatorHotAddressInputSchema
>
export type MaintenancePriceCalculatorHotAddressResult = z.infer<
  typeof maintenancePriceCalculatorHotAddressResultSchema
>
